import React from 'react';

// Paths tuned to read like chunky permanent-marker handwriting at 300×130.
// Lifted verbatim from docs/design/.../nametag.jsx.
const JOE_PATHS = [
  // J — top hat (short horizontal tick)
  { d: 'M 54 26 L 104 22',                                         len: 52,  dur: 160, delay: 0 },
  // J — down + hook
  { d: 'M 82 26 L 86 86 Q 86 108 64 108 Q 40 108 34 88',           len: 150, dur: 560, delay: 180 },
  // o — two-pass oval for marker chunkiness
  { d: 'M 150 56 Q 120 56 118 78 Q 118 102 146 104 Q 176 104 178 82 Q 178 56 152 56', len: 210, dur: 580, delay: 900 },
  // e — horizontal bar + wrap-around curl
  { d: 'M 214 84 Q 252 80 250 68 Q 248 52 228 52 Q 204 52 202 76 Q 202 102 230 104 Q 250 104 260 90', len: 240, dur: 640, delay: 1620 },
];

interface JoeMarkerProps {
  color?: string;
  strokeWidth?: number;
  seed?: number;
}

export default function JoeMarker({
  color = 'currentColor',
  strokeWidth = 18,
  seed = 0,
}: JoeMarkerProps) {
  return (
    <svg
      key={seed}
      viewBox="0 0 300 130"
      width="100%"
      height="100%"
      style={{ overflow: 'visible', display: 'block', filter: 'drop-shadow(0 2px 0 rgba(0,0,0,0.25))' }}
      aria-hidden="true"
    >
      <defs>
        <filter id={`ink-${seed}`} x="-5%" y="-5%" width="110%" height="110%">
          <feTurbulence type="fractalNoise" baseFrequency="1.8" numOctaves={2} seed={seed + 1} />
          <feDisplacementMap in="SourceGraphic" scale={1.2} />
        </filter>
      </defs>
      <g
        filter={`url(#ink-${seed})`}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {JOE_PATHS.map((s, i) => (
          <path
            key={i}
            d={s.d}
            strokeDasharray={s.len}
            strokeDashoffset={s.len}
            style={{
              animation: `jm-${seed}-${i} ${s.dur}ms cubic-bezier(.55,.1,.45,1) ${s.delay}ms forwards`,
            }}
          />
        ))}
      </g>
      {/* Second pass — thinner, lower opacity, simulating uneven marker ink density. */}
      <g
        fill="none"
        stroke={color}
        strokeOpacity={0.35}
        strokeWidth={strokeWidth * 0.55}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {JOE_PATHS.map((s, i) => (
          <path
            key={i}
            d={s.d}
            strokeDasharray={s.len}
            strokeDashoffset={s.len}
            style={{
              animation: `jm2-${seed}-${i} ${s.dur}ms cubic-bezier(.55,.1,.45,1) ${s.delay}ms forwards`,
            }}
          />
        ))}
      </g>
      <style>{JOE_PATHS.map((_, i) => `
        @keyframes jm-${seed}-${i}  { to { stroke-dashoffset: 0; } }
        @keyframes jm2-${seed}-${i} { to { stroke-dashoffset: 0; } }
      `).join('')}</style>
    </svg>
  );
}
